import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const CBCSettingsManager = () => {
  const [settings, setSettings] = useState(null);
  const [presets, setPresets] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [boundaryPromptPreview, setBoundaryPromptPreview] = useState('');
  const [showPromptPreview, setShowPromptPreview] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, presetsRes] = await Promise.all([
        api.get('/admin/cbc/settings'),
        api.get('/admin/cbc/presets')
      ]);

      setSettings(settingsRes.data.settings || {});
      setPresets(presetsRes.data.presets || {});
    } catch (err) {
      console.error('Error fetching CBC settings:', err);
      setError(err.response?.data?.error || 'Failed to load CBC settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchBoundaryPromptPreview = async () => {
    try {
      const res = await api.get('/admin/cbc/boundary-prompt');
      setBoundaryPromptPreview(res.data.prompt);
      setShowPromptPreview(true);
    } catch (err) {
      console.error('Error fetching boundary prompt:', err);
      setError('Failed to load boundary prompt preview');
    }
  };

  const handleToggle = async (enabled) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put('/admin/cbc/toggle', { enabled });

      setSettings(prev => ({ ...prev, enabled }));
      setSuccess(`CBC ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error toggling CBC:', err);
      setError(err.response?.data?.error || 'Failed to toggle CBC');
    } finally {
      setSaving(false);
    }
  };

  const handlePresetChange = async (presetName) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await api.put(`/admin/cbc/preset/${presetName}`, {});

      setSettings(prev => ({ ...prev, thresholdPreset: presetName }));
      setSuccess(`Threshold preset changed to ${presetName}`);
    } catch (err) {
      console.error('Error changing preset:', err);
      setError(err.response?.data?.error || 'Failed to change preset');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsUpdate = async (updates) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const res = await api.put('/admin/cbc/settings', updates);

      setSettings(res.data.settings);
      setSuccess('Settings updated successfully');
    } catch (err) {
      console.error('Error updating settings:', err);
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={styles.container}>Loading CBC settings...</div>;
  }

  const currentPreset = settings?.thresholdPreset || 'moderate';
  const currentThresholds = presets[currentPreset] || {};

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Conversational Boundary Control (CBC)</h2>
      <p style={styles.subtitle}>
        Configure how the AI therapist maintains therapeutic boundaries and responds to conversation drift.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Master Enable/Disable */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>CBC Status</h3>
        <div style={styles.toggleContainer}>
          <label style={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={settings?.enabled !== false}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={saving}
              style={styles.checkbox}
            />
            <span style={styles.toggleText}>
              {settings?.enabled !== false ? 'Enabled' : 'Disabled'}
            </span>
          </label>
          <p style={styles.description}>
            When enabled, the AI will track therapeutic drift and apply boundary-maintaining prompts.
          </p>
        </div>
      </div>

      {/* Classification Method */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Classification Method</h3>
        <p style={styles.description}>
          Choose how client messages are classified as therapeutic or non-therapeutic content.
          This affects drift detection accuracy and response latency.
        </p>
        <select
          value={settings?.classificationMethod || 'inline_prompt'}
          onChange={(e) => handleSettingsUpdate({ classificationMethod: e.target.value })}
          disabled={saving}
          style={styles.select}
        >
          <option value="inline_prompt">
            Inline Prompt - AI includes classification tag in response (recommended, no latency)
          </option>
          <option value="keywords_only">
            Keywords Only - Fast keyword matching (may have false positives)
          </option>
        </select>
        <div style={styles.methodDetails}>
          {settings?.classificationMethod === 'inline_prompt' && (
            <p style={styles.methodNote}>
              <strong>Inline Prompt:</strong> The therapist AI includes a hidden classification
              tag in each response, which is stripped before display. No additional latency,
              but the AI may occasionally forget to include the tag.
            </p>
          )}
          {(!settings?.classificationMethod || settings?.classificationMethod === 'keywords_only') && (
            <p style={styles.methodNote}>
              <strong>Keywords Only:</strong> Fast pattern matching using therapeutic keyword
              detection. May classify casual messages containing common words like "need",
              "want", or "feel" as therapeutic.
            </p>
          )}
        </div>
      </div>

      {/* Threshold Presets */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Threshold Preset</h3>
        <p style={styles.description}>
          Choose how quickly the AI attempts to reconnect when conversations drift from therapeutic content.
        </p>
        <div style={styles.presetGrid}>
          {Object.entries(presets).map(([name, preset]) => (
            <div
              key={name}
              style={{
                ...styles.presetCard,
                ...(currentPreset === name ? styles.presetCardActive : {})
              }}
              onClick={() => !saving && handlePresetChange(name)}
            >
              <h4 style={styles.presetName}>{name.charAt(0).toUpperCase() + name.slice(1)}</h4>
              <p style={styles.presetDescription}>{preset.description}</p>
              <div style={styles.presetThresholds}>
                <div><strong>Observation:</strong> {preset.observationThreshold} exchanges</div>
                <div><strong>Soft reconnect:</strong> {preset.softReconnectionThreshold} exchanges</div>
                <div><strong>Explicit reconnect:</strong> {preset.explicitReconnectionThreshold} exchanges</div>
                <div><strong>Alert:</strong> {preset.alertThreshold} exchanges</div>
              </div>
              {preset.recommendedFor && (
                <p style={styles.recommendedFor}>
                  <em>Recommended for: {preset.recommendedFor}</em>
                </p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Transparency Mode */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Transparency Mode</h3>
        <p style={styles.description}>
          How transparent the AI should be when attempting to reconnect to therapeutic content.
        </p>
        <select
          value={settings?.transparencyMode || 'implicit_then_explicit'}
          onChange={(e) => handleSettingsUpdate({ transparencyMode: e.target.value })}
          disabled={saving}
          style={styles.select}
        >
          <option value="implicit_only">
            Implicit Only - Natural redirection without mentioning role
          </option>
          <option value="implicit_then_explicit">
            Implicit then Explicit - Start naturally, become transparent if needed
          </option>
          <option value="always_explicit">
            Always Explicit - Always transparent about therapeutic role
          </option>
        </select>
      </div>

      {/* Alert Settings */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Alert Settings</h3>
        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.alertSettings?.enableSessionSummaryAlerts !== false}
              onChange={(e) => handleSettingsUpdate({
                alertSettings: { ...settings?.alertSettings, enableSessionSummaryAlerts: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable session summary alerts (drift metrics in session summaries)
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.alertSettings?.enableDashboardFlags !== false}
              onChange={(e) => handleSettingsUpdate({
                alertSettings: { ...settings?.alertSettings, enableDashboardFlags: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable dashboard flags (persistent drift pattern warnings)
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.alertSettings?.enableRealTimeAlerts === true}
              onChange={(e) => handleSettingsUpdate({
                alertSettings: { ...settings?.alertSettings, enableRealTimeAlerts: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable real-time alerts (only for severe patterns like role abandonment)
          </label>
        </div>
      </div>

      {/* Feature Flags */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Feature Flags</h3>
        <div style={styles.checkboxGroup}>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.featureFlags?.enableDriftDetection !== false}
              onChange={(e) => handleSettingsUpdate({
                featureFlags: { ...settings?.featureFlags, enableDriftDetection: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable drift detection
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.featureFlags?.enableReconnectionPrompts !== false}
              onChange={(e) => handleSettingsUpdate({
                featureFlags: { ...settings?.featureFlags, enableReconnectionPrompts: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable reconnection prompts
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.featureFlags?.enableResistanceTracking !== false}
              onChange={(e) => handleSettingsUpdate({
                featureFlags: { ...settings?.featureFlags, enableResistanceTracking: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable resistance pattern tracking
          </label>
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={settings?.featureFlags?.enableTherapistDashboard !== false}
              onChange={(e) => handleSettingsUpdate({
                featureFlags: { ...settings?.featureFlags, enableTherapistDashboard: e.target.checked }
              })}
              disabled={saving}
              style={styles.checkbox}
            />
            Enable therapist dashboard CBC metrics
          </label>
        </div>
      </div>

      {/* Boundary Prompt Preview */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Boundary Prompt Preview</h3>
        <button
          onClick={fetchBoundaryPromptPreview}
          style={styles.button}
          disabled={saving}
        >
          {showPromptPreview ? 'Refresh Preview' : 'Show Current Boundary Prompt'}
        </button>
        {showPromptPreview && boundaryPromptPreview && (
          <pre style={styles.promptPreview}>
            {boundaryPromptPreview}
          </pre>
        )}
      </div>

      {/* Current Thresholds Summary */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Current Configuration Summary</h3>
        <div style={styles.summary}>
          <div><strong>Status:</strong> {settings?.enabled !== false ? 'Active' : 'Disabled'}</div>
          <div><strong>Classification:</strong> {(settings?.classificationMethod || 'keywords_only').replace('_', ' ')}</div>
          <div><strong>Preset:</strong> {currentPreset.charAt(0).toUpperCase() + currentPreset.slice(1)}</div>
          <div><strong>Transparency:</strong> {settings?.transparencyMode || 'implicit_then_explicit'}</div>
          <div><strong>Observation Threshold:</strong> {currentThresholds.observationThreshold || 10} exchanges</div>
          <div><strong>Soft Reconnection:</strong> {currentThresholds.softReconnectionThreshold || 15} exchanges</div>
          <div><strong>Explicit Reconnection:</strong> {currentThresholds.explicitReconnectionThreshold || 20} exchanges</div>
          <div><strong>Alert Threshold:</strong> {currentThresholds.alertThreshold || 30} exchanges</div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1000px',
    margin: '0 auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '10px',
    color: '#333'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '20px'
  },
  section: {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#444'
  },
  description: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '15px'
  },
  error: {
    padding: '12px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    borderRadius: '6px',
    marginBottom: '20px'
  },
  success: {
    padding: '12px',
    backgroundColor: '#d1fae5',
    color: '#059669',
    borderRadius: '6px',
    marginBottom: '20px'
  },
  toggleContainer: {
    marginTop: '10px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer'
  },
  toggleText: {
    fontSize: '16px',
    fontWeight: '500',
    marginLeft: '10px'
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer'
  },
  presetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '15px',
    marginTop: '15px'
  },
  presetCard: {
    padding: '15px',
    backgroundColor: '#fff',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  presetCardActive: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff'
  },
  presetName: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#333'
  },
  presetDescription: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '10px'
  },
  presetThresholds: {
    fontSize: '12px',
    color: '#555',
    lineHeight: '1.6'
  },
  recommendedFor: {
    fontSize: '11px',
    color: '#888',
    marginTop: '10px'
  },
  select: {
    width: '100%',
    padding: '10px',
    fontSize: '14px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    backgroundColor: '#fff'
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  button: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  promptPreview: {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: '#1f2937',
    color: '#e5e7eb',
    borderRadius: '6px',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '400px',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace'
  },
  summary: {
    fontSize: '14px',
    lineHeight: '2',
    color: '#555'
  },
  methodDetails: {
    marginTop: '15px'
  },
  methodNote: {
    padding: '12px',
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#0369a1',
    lineHeight: '1.5'
  }
};

export default CBCSettingsManager;
